import React from "react";
import {
	Button,
	ClassNameProps,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Modal,
	Row,
	Section,
	StyleProps,
} from "@hex-insights/core";
import { EmptyView, SlimEmptyView } from "../../../../Components/Empty";
import { Icon, IconContainer } from "../../../../Components/Icon";
import { Tile } from "../../../../Components/Tile";
import { Person, PersonFilterInput } from "../../../../Utilities";
import { AddressCard, CardAddress } from "../Card";
import { AddressListModal } from "../ListModal";
import styles from "./styles.module.css";

export type AddressListSectionProps = {
	isLoading?: boolean;
	addresses: CardAddress[] | null | undefined;
	columns?: number;
	Container?: typeof Tile | typeof Section;
	emptyView?: "slim" | "full";
	initialCreatePersonIDs?: Person["id"][];
	initialCreatePeopleFilter?: PersonFilterInput;
	createFormNameSuffix?: string;
} & Partial<ClassNameProps & StyleProps>;

export function AddressListSection({
	isLoading = false,
	addresses,
	columns = 1,
	Container = Section,
	emptyView = "slim",
	initialCreatePeopleFilter,
	initialCreatePersonIDs,
	createFormNameSuffix,
	className,
	style,
}: AddressListSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Container className={className} style={style}>
				<Container.Header>
					<Row justify="space-between" align="center">
						<Row justify="spaced-start" align="center">
							<IconContainer color="var(--theme-colors__blue--light)">
								<Icon.House color="var(--theme-colors__blue)" size={18} />
							</IconContainer>

							<Heading.H3 noMargin>Addresses</Heading.H3>
						</Row>

						<Button variant="link" size="small" onClick={openModal} className="button--icon">
							<Icon.Gear size={16} className="block" />
						</Button>
					</Row>
				</Container.Header>
				<Container.Body className={styles["body"]}>
					<Conditional>
						<If condition={isLoading && !addresses}>Loading...</If>
						<If condition={!!addresses && addresses.length === 0}>
							<Conditional>
								<If condition={emptyView === "slim"}>
									<SlimEmptyView>No addresses listed</SlimEmptyView>
								</If>
								<Else>
									<EmptyView
										IconComponent={Icon.House}
										primaryText="Addresses"
										secondaryText="No addresses listed"
										style={{ height: "100%" }}
									/>
								</Else>
							</Conditional>
						</If>
						<Else>
							<Grid tracks={columns} gap="1rem">
								{addresses?.map((address) => (
									<AddressCard key={address.id} address={address} slim />
								))}
							</Grid>
						</Else>
					</Conditional>
				</Container.Body>
			</Container>

			{!!addresses && (
				<Modal.If condition={isModalOpen}>
					<AddressListModal
						addresses={addresses}
						initialCreatePersonIDs={initialCreatePersonIDs}
						initialCreatePeopleFilter={initialCreatePeopleFilter}
						createFormNameSuffix={createFormNameSuffix}
						onClose={closeModal}
					/>
				</Modal.If>
			)}
		</React.Fragment>
	);
}
